import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import LanguageSelector from "./LanguageSelector";
import { Menu, X } from "lucide-react";
import logo from '@/assets/logo.webp';

const PublicMainNav = () => {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductMenuOpen, setIsProductMenuOpen] = useState(false);
  const [isResourcesMenuOpen, setIsResourcesMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close the mobile menu when clicking outside or changing screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    // Prevent scrolling when mobile menu is open
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <nav className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <div className="flex-shrink-0 flex items-center">
              <a href="/"><img src={logo} alt="FitCalendar" height="32px" width="207px" className="h-8 object-contain" /></a>
          </div>

          {/* Mobile burger menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-md text-gray-600 hover:text-primary hover:bg-gray-100 focus:outline-none"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
          <div className="flex items-center space-x-8">
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-lg font-medium text-gray-700 hover:text-primary">
                {t('nav.home')}
              </Link>
              <div className="relative group">
                <button className="text-lg font-medium text-gray-700 hover:text-primary flex items-center">
                  {t('nav.product')}
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute left-0 mt-2 pt-2 mb-2 pb-2 w-60 bg-white shadow-lg border rounded-md opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transform transition duration-200 ease-in-out z-10">
                  <a href="/product/yoga-studio-software/" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                    {t('nav.yogaStudioSoftware')}
                  </a>
                  <a href="/product/pilates-studio-software/" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                    {t('nav.pilatesStudioSoftware')}
                  </a>
                  <a href="/product/cantienica-studio-software/" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                    {t('nav.cantienicaStudioSoftware')}
                  </a>
                </div>
              </div>
              <div className="relative group">
                <button className="text-lg font-medium text-gray-700 hover:text-primary flex items-center">
                  {t('nav.resources') || "Resources"}
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute left-0 mt-2 pt-2 pb-2 w-60 bg-white shadow-lg border rounded-md opacity-0 group-hover:opacity-100 transform group-hover:translate-y-0 transition duration-200 ease-in-out z-10">
                  <a href="/blog/" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                    {t('nav.blog')}
                  </a>
                  <a href="/guides/" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                    {t('nav.guides')}
                  </a>
                  {/* <Link to="/faq" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                    FAQ
                  </Link> */}
                </div>
              </div>
              <Link to="/pricing/" className="text-lg font-medium text-gray-700 hover:text-primary">
                {t('nav.pricing')}
              </Link>
              <Link to="/affiliate-program/" className="text-lg font-medium text-gray-700 hover:text-primary">
                {t('nav.affiliate')}
              </Link>
              <Link to="/contact/" className="text-lg font-medium text-gray-700 hover:text-primary">
                {t('nav.contact')}
              </Link>
            </div>
            <LanguageSelector />
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-50 overflow-y-auto bg-white">
            <div className="p-4 flex justify-between items-center border-b border-gray-200">
              <h1 className="text-2xl font-bold text-primary">
                <a href="/" onClick={() => setIsMobileMenuOpen(false)}>
                  <img src={logo} alt="FitCalendar" className="h-8 object-contain" />
                </a>
              </h1>
              <button
                onClick={toggleMobileMenu}
                className="p-2 rounded-md text-gray-600 hover:text-primary hover:bg-gray-100 focus:outline-none"
              >
                <X size={24} />
              </button>
            </div>

            {/* State for Product submenu */}
            <div className="flex flex-col p-4">
              <Link
                to="/"
                className="px-3 py-4 text-lg font-medium text-gray-700 hover:text-primary border-b border-gray-100"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.home')}
              </Link>

              {/* Product dropdown toggle */}
              <div className="border-b border-gray-100">
                <button
                  onClick={() => setIsProductMenuOpen(prev => !prev)}
                  className="w-full text-left px-3 py-4 text-lg font-medium text-gray-700 hover:text-primary flex justify-between items-center"
                >
                  {t('nav.product')}
                  <svg
                    className={`w-5 h-5 transform transition-transform duration-200 ${isProductMenuOpen ? 'rotate-180' : ''
                      }`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Submenu */}
                {isProductMenuOpen && (
                  <div className="pl-6 pb-2">
                    <a
                      href="/product/yoga-studio-software/"
                      className="block py-2 text-gray-700 hover:text-primary"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t('nav.yogaStudioSoftware')}
                    </a>
                    <a
                      href="/product/pilates-studio-software/"
                      className="block py-2 text-gray-700 hover:text-primary"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t('nav.pilatesStudioSoftware')}
                    </a>
                    <a
                      href="/product/cantienica-studio-software/"
                      className="block py-2 text-gray-700 hover:text-primary"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t('nav.cantienicaStudioSoftware')}
                    </a>
                  </div>
                )}
              </div>

              {/* Resources dropdown toggle */}
              <div className="border-b border-gray-100">
                <button
                  onClick={() => setIsResourcesMenuOpen(prev => !prev)}
                  className="w-full text-left px-3 py-4 text-lg font-medium text-gray-700 hover:text-primary flex justify-between items-center"
                >
                  {t('nav.resources')}
                  <svg
                    className={`w-5 h-5 transform transition-transform duration-200 ${isResourcesMenuOpen ? 'rotate-180' : ''
                      }`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Submenu */}
                {isResourcesMenuOpen && (
                  <div className="pl-6 pb-2">
                    <a
                      href="/blog/"
                      className="block py-2 text-gray-700 hover:text-primary"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t('nav.blog')}
                    </a>
                    <a
                      href="/guides/"
                      className="block py-2 text-gray-700 hover:text-primary"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t('nav.guides')}
                    </a>
                  </div>
                )}
              </div>
              <Link
                to="/pricing/"
                className="px-3 py-4 text-lg font-medium text-gray-700 hover:text-primary border-b border-gray-100"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.pricing')}
              </Link>
              <Link
                to="/affiliate-program/"
                className="px-3 py-4 text-lg font-medium text-gray-700 hover:text-primary border-b border-gray-100"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.affiliate')}
              </Link>
              <Link
                to="/contact/"
                className="px-3 py-4 text-lg font-medium text-gray-700 hover:text-primary"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.contact')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default PublicMainNav;